import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { BehaviorSubject, tap, filter, forkJoin } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Vacations, VacationDates, Response, HolidayData, TotalLeavesData, DurationDays } from '../interfaces';
import { SHOW_LOADER } from '../interceptors/loader.interceptor';

@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  vacationsList$: BehaviorSubject<Vacations[]> = new BehaviorSubject<Vacations[]>([]);
  requestedVacationsList$: BehaviorSubject<Vacations[] | null> = new BehaviorSubject<Vacations[] | null>(null);
  holidaysByYearList$: BehaviorSubject<HolidayData[] | null> = new BehaviorSubject<HolidayData[] | null>(null);
  holidaysAllList$: BehaviorSubject<HolidayData[] | null> = new BehaviorSubject<HolidayData[] | null>(null);
  totalLeaves$: BehaviorSubject<TotalLeavesData | null> = new BehaviorSubject<TotalLeavesData | null>(null);

  constructor(private readonly http: HttpClient) {}

  updateVacationsList() {
    return forkJoin({
      vacationsList: this.getVacationsListRequest(),
      totalLeaves: this.getTotalLeavesRequest()
    })
      .pipe(
        filter((responses) => responses.vacationsList.success && responses.totalLeaves.success),
        tap((responses) => {
          this.vacationsList$.next(responses.vacationsList.data!);
          this.totalLeaves$.next(responses.totalLeaves.data!);
        })
      )
      .subscribe();
  }

  updateRequestedVacationsList(isShowLoader: boolean = true) {
    this.getRequestedVacationsListRequest(isShowLoader)
      .pipe(
        filter((response) => response.success),
        tap((response) => {
          this.requestedVacationsList$.next(response.data!);
        })
      )
      .subscribe();
  }

  updateHolidaysByYearList(year: number) {
    return this.getHolidaysByYearListRequest(year)
      .pipe(
        filter((response) => response.success),
        tap((response) => {
          this.holidaysByYearList$.next(response.data!);
        })
      )
      .subscribe();
  }

  updateAllHolidaysList() {
    return this.getAllHolidaysListRequest()
      .pipe(
        filter((response) => response.success),
        tap((response) => {
          this.holidaysAllList$.next(response.data!);
        })
      )
      .subscribe();
  }

  getVacationsListRequest() {
    return this.http.get<Response<Vacations[]>>(`${environment.baseUrl}/vacations`);
  }

  getTotalLeavesRequest() {
    return this.http.get<Response<TotalLeavesData>>(`${environment.baseUrl}/vacations/me/stat`);
  }

  getRequestedVacationsListRequest(isShowLoader: boolean) {
    return this.http.get<Response<Vacations[]>>(`${environment.baseUrl}/vacations/requested`, {
      context: new HttpContext().set(SHOW_LOADER, isShowLoader)
    });
  }

  createVacationRequest(vacationDates: VacationDates) {
    const params = vacationDates.employeeId ? `/${vacationDates.employeeId}` : '';
    return this.http.post<Response<Vacations>>(`${environment.baseUrl}/vacations/request${params}`, vacationDates);
  }

  approveVacationRequest(id: number) {
    return this.http.post<Response<any>>(`${environment.baseUrl}/vacations/approve/${id}`, null);
  }

  declineVacationRequest(id: number) {
    return this.http.post<Response<any>>(`${environment.baseUrl}/vacations/decline/${id}`, null);
  }

  removeVacationRequest(id: number) {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/vacations/${id}`);
  }

  removeVacationByManagerRequest(id: number) {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/vacations/manager-delete/${id}`);
  }

  getHolidaysByYearListRequest(year: number) {
    return this.http.get<Response<HolidayData[]>>(`${environment.baseUrl}/holiday/${year}`);
  }

  getAllHolidaysListRequest() {
    return this.http.get<Response<HolidayData[]>>(`${environment.baseUrl}/holiday/all`);
  }

  createHolidayRequest(data: HolidayData) {
    return this.http.post<Response<Vacations>>(`${environment.baseUrl}/holiday`, data);
  }

  removeHolidayRequest(id: number) {
    return this.http.delete<Response<any>>(`${environment.baseUrl}/holiday?id=${id}`);
  }

  getLeaveDurationRequest(data: VacationDates) {
    const params = new HttpParams().set('startDate', data.startDate.toISOString()).set('endDate', data.endDate.toISOString()).set('type', data.type);

    return this.http.get<Response<DurationDays>>(`${environment.baseUrl}/vacations/request/duration`, {
      params
    });
  }

  clearData(): void {
    this.vacationsList$.next([]);
    this.requestedVacationsList$.next(null);
    this.holidaysByYearList$.next(null);
    this.holidaysAllList$.next(null);
    this.totalLeaves$.next(null);
  }
}
